import { useRef, useEffect, useCallback } from 'react';
import { useGameStore } from '@/store/gameStore';
import { CanvasRenderer } from '@/renderer/CanvasRenderer';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';

interface GameBoardProps {
  hintHighlightNodeId?: string | null;
  hintSequence?: string[];
}

export function GameBoard({ hintHighlightNodeId = null, hintSequence = [] }: GameBoardProps) {
  const board = useGameStore((s) => s.board);
  const harmonyReport = useGameStore((s) => s.harmonyReport);
  const lastStrikeResult = useGameStore((s) => s.lastStrikeResult);
  const isComplete = useGameStore((s) => s.isComplete);
  const strikeNode = useGameStore((s) => s.strikeNode);
  const colorBlindMode = useGameStore((s) => s.colorBlindMode);
  const reducedMotion = useGameStore((s) => s.reducedMotion);
  const showFrequencyNumbers = useGameStore((s) => s.showFrequencyNumbers);

  const focusedNodeIndex = useKeyboardNavigation();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<CanvasRenderer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevBoardRef = useRef<ReturnType<typeof useGameStore.getState>['board']>(null);

  // Initialize renderer and handle resize
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function updateSize() {
      if (!canvas || !container || !ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Set canvas buffer size (high-DPI)
      canvas.width = width * dpr;
      canvas.height = height * dpr;

      // Set canvas display size via CSS
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Scale context for high-DPI
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (rendererRef.current) {
        rendererRef.current.resize(width, height);
      } else {
        rendererRef.current = new CanvasRenderer(ctx, width, height);
      }

      // Re-render after resize
      const currentBoard = useGameStore.getState().board;
      const currentReport = useGameStore.getState().harmonyReport;
      if (currentBoard && rendererRef.current && !rendererRef.current.isAnimating()) {
        rendererRef.current.render(currentBoard, currentReport);
      }
    }

    updateSize();

    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Sync accessibility settings to the renderer
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.setColorBlindMode(colorBlindMode);
      // Re-render with new palette
      const currentBoard = useGameStore.getState().board;
      const currentReport = useGameStore.getState().harmonyReport;
      if (currentBoard && !rendererRef.current.isAnimating()) {
        rendererRef.current.render(currentBoard, currentReport);
      }
    }
  }, [colorBlindMode]);

  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.setReducedMotion(reducedMotion);
    }
  }, [reducedMotion]);

  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.setShowFrequencyNumbers(showFrequencyNumbers);
      // Re-render with updated setting
      const currentBoard = useGameStore.getState().board;
      const currentReport = useGameStore.getState().harmonyReport;
      if (currentBoard && !rendererRef.current.isAnimating()) {
        rendererRef.current.render(currentBoard, currentReport);
      }
    }
  }, [showFrequencyNumbers]);

  // Sync focused node index to renderer
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.focusedNodeIndex = focusedNodeIndex;
      // Re-render to show/hide focus ring
      const currentBoard = useGameStore.getState().board;
      const currentReport = useGameStore.getState().harmonyReport;
      if (currentBoard && !rendererRef.current.isAnimating()) {
        rendererRef.current.render(currentBoard, currentReport);
      }
    }
  }, [focusedNodeIndex]);

  // Sync hint properties to renderer
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.hintHighlightNodeId = hintHighlightNodeId ?? null;
      rendererRef.current.hintSequence = hintSequence;
      // Re-render to show/hide hints
      const currentBoard = useGameStore.getState().board;
      const currentReport = useGameStore.getState().harmonyReport;
      if (currentBoard && !rendererRef.current.isAnimating()) {
        rendererRef.current.render(currentBoard, currentReport);
      }
    }
  }, [hintHighlightNodeId, hintSequence]);

  // Handle strike animation or normal render when board/report changes
  useEffect(() => {
    if (!board || !rendererRef.current) return;

    const renderer = rendererRef.current;

    // If there's a new strike result and we have the pre-strike board, animate
    if (lastStrikeResult && prevBoardRef.current && !renderer.isAnimating()) {
      const preBoard = prevBoardRef.current;
      renderer.animateStrike(preBoard, lastStrikeResult, harmonyReport, () => {
        // After strike animation, play celebration if puzzle is solved
        if (isComplete && board) {
          renderer.playCelebration(board, harmonyReport);
        }
      });
    } else if (!renderer.isAnimating()) {
      // No animation - just render
      renderer.render(board, harmonyReport);
    }

    // Track the current board for next comparison
    prevBoardRef.current = board;
  }, [board, harmonyReport, lastStrikeResult, isComplete]);

  // Click handler
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || !board || !rendererRef.current) return;

      // Don't allow clicks during animation
      if (rendererRef.current.isAnimating()) return;

      const rect = canvas.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;

      const nodeId = rendererRef.current.hitTest(canvasX, canvasY, board);
      if (nodeId) {
        strikeNode(nodeId);
      }
    },
    [board, strikeNode],
  );

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label="Game board"
      tabIndex={0}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        outline: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        style={{
          display: 'block',
          cursor: 'pointer',
        }}
      />
    </div>
  );
}
